import { Link } from '@tanstack/react-router'
import type { ComponentPropsWithoutRef } from 'react'

import { cn } from '../../../_lib/utils'

const Sidebar = ({
  children,
  className,
}: ComponentPropsWithoutRef<'aside'> & { collapsed?: boolean }) => {
  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-brand-grayscale-4 bg-brand-grayscale-2 transition-[width] duration-200',
        className,
      )}
    >
      {children}
    </aside>
  )
}

const SidebarHeader = ({ children, className }: ComponentPropsWithoutRef<'header'>) => {
  return (
    <header
      className={cn(
        'flex h-14 shrink-0 items-center border-b border-brand-grayscale-4 px-5',
        className,
      )}
    >
      {children}
    </header>
  )
}

const SidebarHeaderImage = ({ alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
  return <img width={95} height={20} alt={alt} {...props} />
}

const SidebarItems = ({ children, className }: ComponentPropsWithoutRef<'nav'>) => {
  return <nav className={cn('flex flex-col gap-0.5 px-4 pt-4 pb-2', className)}>{children}</nav>
}

const SidebarContent = ({ children, className }: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex h-full flex-col justify-between', className)}>{children}</div>
}

type SidebarButtonProps = {
  href: string
  isActive: boolean
  children: React.ReactNode
  className?: string
}

const SidebarButton = ({ children, className, href, isActive }: SidebarButtonProps) => {
  return (
    <Link
      to={href as any}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2.5 text-brand-grayscale-7 transition-colors overflow-hidden',
        isActive
          ? 'bg-brand-green-primary-1 font-semibold text-brand-green-primary-5'
          : 'font-medium hover:bg-brand-grayscale-3',
        className,
      )}
    >
      {children}
    </Link>
  )
}

const SidebarButtonImage = ({ alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
  return <img width={22} height={22} alt={alt} {...props} />
}

const SidebarButtonText = ({ children, className, ...props }: ComponentPropsWithoutRef<'span'>) => {
  return (
    <span className={cn('text-[15px] font-medium', className)} {...props}>
      {children}
    </span>
  )
}

export {
  Sidebar,
  SidebarButton,
  SidebarButtonImage,
  SidebarButtonText,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderImage,
  SidebarItems,
}
